import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { type WorkspaceAgentActionsMenuOpenedLocation } from '@air/analytics';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PrivateWorkspaceAgentActionsMenuOpenContextValue = {
  actionsMenuEntryLocation: WorkspaceAgentActionsMenuOpenedLocation;
  isOpen: boolean;
  openActionsMenu: (location: WorkspaceAgentActionsMenuOpenedLocation) => void;
  setIsOpen: (isOpen: boolean) => void;
};

const defaultPrivateWorkspaceAgentActionsMenuOpenContextValue: PrivateWorkspaceAgentActionsMenuOpenContextValue = {
  actionsMenuEntryLocation: 'composer_button',
  isOpen: false,
  openActionsMenu: () => undefined,
  setIsOpen: () => undefined,
};

const PrivateWorkspaceAgentActionsMenuOpenContext = createContext<PrivateWorkspaceAgentActionsMenuOpenContextValue>(
  defaultPrivateWorkspaceAgentActionsMenuOpenContextValue,
);

export type PrivateWorkspaceAgentActionsMenuOpenProviderProps = {
  children: ReactNode;
};

/** Composer Actions menu open state — shared by the footer trigger and zero-state chips (not Zustand). */
export const PrivateWorkspaceAgentActionsMenuOpenProvider = memo(
  ({ children }: PrivateWorkspaceAgentActionsMenuOpenProviderProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [actionsMenuEntryLocation, setActionsMenuEntryLocation] =
      useState<WorkspaceAgentActionsMenuOpenedLocation>('composer_button');

    const handleSetIsOpen = useCallback((nextOpen: boolean) => {
      if (nextOpen) {
        setActionsMenuEntryLocation('composer_button');
      }

      setIsOpen(nextOpen);
    }, []);

    const openActionsMenu = useCallback((location: WorkspaceAgentActionsMenuOpenedLocation) => {
      setActionsMenuEntryLocation(location);
      setIsOpen(true);
    }, []);

    const value = useMemo(
      (): PrivateWorkspaceAgentActionsMenuOpenContextValue => ({
        actionsMenuEntryLocation,
        isOpen,
        openActionsMenu,
        setIsOpen: handleSetIsOpen,
      }),
      [actionsMenuEntryLocation, isOpen, handleSetIsOpen, openActionsMenu],
    );

    return (
      <PrivateWorkspaceAgentActionsMenuOpenContext.Provider value={value}>
        {children}
      </PrivateWorkspaceAgentActionsMenuOpenContext.Provider>
    );
  },
);

PrivateWorkspaceAgentActionsMenuOpenProvider.displayName = 'PrivateWorkspaceAgentActionsMenuOpenProvider';

export const usePrivateWorkspaceAgentActionsMenuOpen = (): PrivateWorkspaceAgentActionsMenuOpenContextValue => {
  const value = useContext(PrivateWorkspaceAgentActionsMenuOpenContext);

  if (value === defaultPrivateWorkspaceAgentActionsMenuOpenContextValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentActionsMenuOpen must be used within PrivateWorkspaceAgentActionsMenuOpenProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      context: 'Workspace agent actions menu open context not found',
      error,
    });
  }

  return value;
};
